/* /assets/smooth-scroll.js
   Global smooth scroll (Lenis-style): eases the REAL window scroll position
   toward a target via requestAnimationFrame + lerp, instead of jumping
   instantly on every wheel tick. Desktop/trackpad only — touch devices keep
   fully native scrolling untouched.

   Deliberately NOT a virtual/transform-based scroller (no wrapper div gets
   translateY'd). It drives real window.scrollTo() every frame, so anything
   that depends on native scroll — position:sticky, the theme's own
   window.scrollY-based sticky header, IntersectionObserver-based lazy
   loading, the scroll-lock library used by drawers/modals, keyboard
   navigation, anchor links — keeps working exactly as before. This is also
   why no external library (Lenis et al.) was added: the theme already ships
   ~28k lines of bundled JS and a body-scroll-lock library with its own
   event contract (theme:scroll:lock/unlock, data-scroll-locked on <html>);
   a small engine that explicitly respects that contract is safer and
   lighter than bringing in a general-purpose library and reconciling it
   with theme.js's assumptions.

   Fully self-contained and independently removable: delete the single
   <script src="smooth-scroll.js"> tag in layout/theme.liquid to fully
   revert to native scrolling, nothing else depends on this file. */
(function () {
  'use strict';

  var LERP = 0.1; // 0-1, how much of the remaining distance to close per frame — subtle, not sluggish
  var WHEEL_MULTIPLIER = 1;
  var SETTLE_THRESHOLD = 0.5; // px — stop the rAF loop once this close to target

  var reduceMotionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
  var desktopInputQuery = window.matchMedia('(hover: hover) and (pointer: fine)');

  var current = 0;
  var target = 0;
  var lastSetY = 0;
  var rafId = null;
  var active = false;

  function isDesignMode() {
    return !!(window.Shopify && window.Shopify.designMode);
  }

  function shouldRun() {
    return !reduceMotionQuery.matches && desktopInputQuery.matches && !isDesignMode();
  }

  function getScrollY() {
    return window.scrollY || document.documentElement.scrollTop || 0;
  }

  function maxScroll() {
    return Math.max(0, document.documentElement.scrollHeight - window.innerHeight);
  }

  function clamp(value, min, max) {
    return Math.min(Math.max(value, min), max);
  }

  function isScrollLocked() {
    return document.documentElement.hasAttribute('data-scroll-locked');
  }

  /* Wheel events over a nested scrollable element (drawer body, dropdown,
     tab panel, etc.) must scroll that element natively, not the page. */
  function isInsideNestedScroller(el) {
    while (el && el !== document.body && el !== document.documentElement) {
      var style = window.getComputedStyle(el);
      var overflowY = style.overflowY;
      if ((overflowY === 'auto' || overflowY === 'scroll') && el.scrollHeight > el.clientHeight) {
        return true;
      }
      el = el.parentElement;
    }
    return false;
  }

  function setScrollBehaviorOverride(auto) {
    /* html{scroll-behavior:smooth} in theme.css would otherwise re-animate
       every single scrollTo() call this engine makes each frame, on top of
       the lerp itself, causing a compounding/laggy feel. Inline style wins
       the cascade, so this cleanly suspends it only while the engine drives
       scroll, without editing the stylesheet. */
    document.documentElement.style.scrollBehavior = auto ? 'auto' : '';
  }

  function applyScroll(y) {
    setScrollBehaviorOverride(true);
    window.scrollTo(0, y);
    lastSetY = y;
  }

  function resyncIfExternalScrollHappened() {
    /* Keyboard nav (PageUp/Down, Home/End, Space, Tab-focus), anchor jumps,
       or any other code calling scrollTo/scrollIntoView moves the real
       scroll position without going through this engine. Adopt it instead
       of fighting it on the next wheel tick. */
    var realY = getScrollY();
    if (Math.abs(realY - lastSetY) > 1) {
      current = realY;
      target = realY;
      lastSetY = realY;
    }
  }

  function tick() {
    current += (target - current) * LERP;

    if (Math.abs(target - current) < SETTLE_THRESHOLD) {
      current = target;
      applyScroll(current);
      rafId = null;
      return;
    }

    applyScroll(current);
    rafId = window.requestAnimationFrame(tick);
  }

  function startLoop() {
    if (rafId) return;
    rafId = window.requestAnimationFrame(tick);
  }

  function onWheel(event) {
    if (!active || isScrollLocked()) return;
    if (event.ctrlKey) return; // pinch-zoom gesture, not scroll
    if (Math.abs(event.deltaY) <= Math.abs(event.deltaX)) return; // horizontal intent (rails/tabs)
    if (isInsideNestedScroller(event.target)) return; // let the nested container scroll natively

    event.preventDefault();

    if (!rafId) resyncIfExternalScrollHappened();

    target += event.deltaY * WHEEL_MULTIPLIER;
    target = clamp(target, 0, maxScroll());

    startLoop();
  }

  /* Reuse the same in-page-link discovery the theme already uses
     (theme.js bindInPageLinks) so hash navigation eases smoothly through
     this engine instead of jumping instantly once native CSS smoothing is
     suspended above. */
  function onAnchorClick(event) {
    if (!active || isScrollLocked()) return;
    var link = event.target.closest('a[href^="#"]');
    if (!link || link.hash === '' || link.hash === '#') return;

    var el;
    try {
      el = document.getElementById(link.hash.slice(1));
    } catch (e) {
      return;
    }
    if (!el) return;

    event.preventDefault();
    var destination = el.getBoundingClientRect().top + getScrollY();
    if (!rafId) resyncIfExternalScrollHappened();
    target = clamp(destination, 0, maxScroll());
    startLoop();
  }

  function enable() {
    if (active) return;
    active = true;
    current = target = lastSetY = getScrollY();
    window.addEventListener('wheel', onWheel, { passive: false });
    document.addEventListener('click', onAnchorClick);
  }

  function disable() {
    if (!active) return;
    active = false;
    if (rafId) {
      window.cancelAnimationFrame(rafId);
      rafId = null;
    }
    window.removeEventListener('wheel', onWheel, { passive: false });
    document.removeEventListener('click', onAnchorClick);
    setScrollBehaviorOverride(false);
  }

  function evaluate() {
    if (shouldRun()) {
      enable();
    } else {
      disable();
    }
  }

  evaluate();
  reduceMotionQuery.addEventListener('change', evaluate);
  desktopInputQuery.addEventListener('change', evaluate);
})();
